import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  CircularProgress,
  Grid,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Box,
  Switch,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Loader2, RefreshCw } from "lucide-react";
import {
  getCurrencyConvertationRateAction,
  getGlobalSettingsAction,
  updateGlobalSettingAction,
} from "../../Actions/SettingAction";
import {
  getTenantCurrenciesAction,
  editCurrencyRatesAction,
} from "../../Actions/TenantActions";
import CustomSnackbar from "../Common/snackBar";
import { CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import CryptoJS from "crypto-js";
import { getPermissionData, getUserData } from "../../Actions/common";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords, containsBadWordsInEmail } from "../../utils/badWord";

const { VALIDATION } = FRONTEND_MESSAGES;

const SMTP_HOST_REGEX = /^(?=.{3,253}$)(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const SITE_ID_REGEX = /^[a-zA-Z0-9_\-]+$/;
const DIGITS_ONLY_REGEX = /^\d+$/;
const POSITIVE_DECIMAL_REGEX = /^\d+(\.\d+)?$/;

const validationSchema = yup.object({
  RESEND_OTP_TIME: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Resend OTP time"))
    .matches(DIGITS_ONLY_REGEX, VALIDATION.NUMERIC_ONLY("Resend OTP time"))
    .max(3, VALIDATION.MAX_LENGTH("Resend OTP time", 3)),
  LOGOUT_TIME_IN_MINUTE: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Logout time"))
    .matches(DIGITS_ONLY_REGEX, VALIDATION.NUMERIC_ONLY("Logout time"))
    .max(3, VALIDATION.MAX_LENGTH("Logout time", 3)),
  ORGANIZATION_MID: yup.string()
    .required(VALIDATION.REQUIRED_FIELD("Plug & Play MID"))
    .test(
      "bad-word",
      "Plug & Play MID contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),

  ORGANIZATION_ACCESS_TOKEN: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Plug & Play Access Token"))
    .test(
      "bad-word",
      "Plug & Play Access Token contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),
  USD_EXCHANGE_RATE: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("USD exchange rate"))
    .matches(POSITIVE_DECIMAL_REGEX, VALIDATION.POSITIVE_DECIMAL("USD exchange rate")),
  INR_EXCHANGE_RATE: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("INR exchange rate"))
    .matches(POSITIVE_DECIMAL_REGEX, VALIDATION.POSITIVE_DECIMAL("INR exchange rate")),
  MAILER: yup.string().required(VALIDATION.REQUIRED_FIELD("Mailer type")),
  SMTP_HOST: yup.string().when("MAILER", {
    is: "SMTP",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("SMTP Host"))
        .matches(SMTP_HOST_REGEX, VALIDATION.INVALID_FORMAT("SMTP Host")),
    otherwise: (schema) => schema.notRequired(),
  }),
  SMTP_PORT: yup.string().when("MAILER", {
    is: "SMTP",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("SMTP Port"))
        .matches(DIGITS_ONLY_REGEX, VALIDATION.NUMERIC_ONLY("SMTP Port"))
        .test("port-range", VALIDATION.RANGE_VALID("SMTP Port", 1, 65535), (val) => {
          const num = parseInt(val, 10);
          return num >= 1 && num <= 65535;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  SMTP_USER: yup.string().when("MAILER", {
    is: "SMTP",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("SMTP User"))
        .max(70, VALIDATION.MAX_LENGTH("SMTP User", 70))
        .email(VALIDATION.INVALID_EMAIL),
    otherwise: (schema) => schema.notRequired(),
  }),
  SMTP_PASSWORD: yup.string().when("MAILER", {
    is: "SMTP",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("SMTP Password"))
        .test("no-spaces", VALIDATION.NO_SPACES("SMTP Password"), (val) => !/\s/.test(val)),
    otherwise: (schema) => schema.notRequired(),
  }),
  SMTP_ENCRYPTION_TYPE: yup.string().when("MAILER", {
    is: "SMTP",
    then: (schema) =>
      schema.required(VALIDATION.REQUIRED_FIELD("Encryption Type")),
    otherwise: (schema) => schema.notRequired(),
  }),
  MAIL_API_URL: yup.string().when("MAILER", {
    is: "Mail API",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("API URL"))
        .url(VALIDATION.INVALID_URL)
        .test(
          "bad-word",
          "API URL contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  MAIL_API_TOKEN: yup.string().when("MAILER", {
    is: "Mail API",
    then: (schema) => schema.required(VALIDATION.REQUIRED_FIELD("Token"))
      .test(
        "bad-word",
        "Token contains inappropriate language.",
        (value) => !containsBadWords(value || "")
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
  MAIL_API_SITE_ID: yup.string().when("MAILER", {
    is: "Mail API",
    then: (schema) =>
      schema
        .required(VALIDATION.REQUIRED_FIELD("Site ID"))
        .matches(SITE_ID_REGEX, VALIDATION.INVALID_FORMAT("Site ID")),
    otherwise: (schema) => schema.notRequired(),
  }),
  ADMIN_EMAIL: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Admin email"))
    .max(70, VALIDATION.MAX_LENGTH("Admin email", 70))
    .email(VALIDATION.INVALID_EMAIL)
    .test(
      "bad-email",
      "Admin email contains inappropriate language.",
      (value) => !containsBadWordsInEmail(value || "")
    ),
});

const getPerPageRecords = () => {
  const decryptedData = CryptoJS.AES.decrypt(
    localStorage.getItem("GLOBAL_SETTING"),
    CRYPTO_TOKEN_SECRET_KEY
  ).toString(CryptoJS.enc.Utf8);
  const data = JSON.parse(decryptedData);
  return data ? data : null;
};

// Styling Object presets for MUI inputs (Enterprise Minimalist style)
const TEXTFIELD_STYLE = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#94a3b8", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": { fontSize: "13px", padding: "10px 14px" }
};

const SELECT_STYLE = {
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8", borderWidth: "1px" },
  "& .MuiSelect-select": { fontSize: "13px", padding: "10px 14px" }
};

const SWITCH_STYLE = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#0f172a",
    "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.08)" },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#0f172a",
  },
};

const SettingsSection = () => {
  const [globalSettings, setGlobalSettings] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [data, setData] = useState([]);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showMailApiToken, setShowMailApiToken] = useState(false);
  const [currencyRates, setCurrencyRates] = useState({
    usd: { id: null, rate: "1.1900" },
    inr: { id: null, rate: "105.8500" },
  });
  const [currentRates, setCurrentRates] = useState({
    usd: "1.1900",
    inr: "105.8500",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const userData = localStorage.getItem("userData") ? getUserData() : null;

  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "settings"
  );

  const Loader = () => (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(2px)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      gap: "12px"
    }}>
      <Loader2 className="h-10 w-10 animate-spin" style={{ color: "#0f172a" }} />
      <span style={{ fontSize: "14px", fontWeight: 600, color: "#334155", fontFamily: "var(--app-font)" }}>
        Updating configurations...
      </span>
    </div>
  );

  const fetchCurrencyRates = async () => {
    try {
      const res = await getTenantCurrenciesAction();
      const currencies = res?.data.data || [];
      const usdCurrency = currencies.find((c) => c.symbol === "$");
      const inrCurrency = currencies.find((c) => c.symbol === "₹");

      const usdRate = usdCurrency?.exchangeRate || "1.1900";
      const inrRate = inrCurrency?.exchangeRate || "105.8500";

      const newCurrencyRates = {
        usd: {
          id: usdCurrency?.id || 1,
          rate: usdRate,
        },
        inr: {
          id: inrCurrency?.id || 3,
          rate: inrRate,
        },
      };

      setCurrencyRates(newCurrencyRates);
      setCurrentRates({
        usd: usdRate,
        inr: inrRate,
      });

      return newCurrencyRates;
    } catch (error) {
      console.error("Error fetching currency rates:", error);
      throw error;
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const res = await getGlobalSettingsAction();
      const settings = res.data.settings;

      const settingsArray = Object.entries(settings || {}).map(
        ([name, value]) => ({
          name,
          value,
        })
      );

      setData(settingsArray);
      setGlobalSettings(settings);

      // Update localStorage
      const encryptedGlobalSettings = CryptoJS.AES.encrypt(
        JSON.stringify(settings),
        CRYPTO_TOKEN_SECRET_KEY
      ).toString();
      localStorage.setItem(`GLOBAL_SETTING`, encryptedGlobalSettings);

      return settings;
    } catch (error) {
      console.error("Error fetching global settings:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);

      try {
        // Fetch global settings
        const localSettings = localStorage.getItem("GLOBAL_SETTING");
        if (localSettings) {
          setGlobalSettings(getPerPageRecords());
        } else {
          await fetchGlobalSettings();
        }

        // Fetch currency rates
        await fetchCurrencyRates();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message,
          severity: "error",
        });
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchLatestRates = async () => {
    setRateLoading(true);

    try {
      const res = await getCurrencyConvertationRateAction();
      const kyd = res?.data?.data?.KYD || 0.85;
      const inr = res?.data?.data?.INR || 88.0;

      // Calculate rates
      const usdRate = (1 / kyd).toFixed(2);
      const inrRate = (inr / kyd).toFixed(2);

      // Update current rates display (readonly)
      setCurrentRates({
        usd: usdRate,
        inr: inrRate,
      });

      // Update editable textboxes
      formik.setFieldValue("USD_EXCHANGE_RATE", usdRate);
      formik.setFieldValue("INR_EXCHANGE_RATE", inrRate);

      setSnackbar({
        open: true,
        message: "Latest currency rates fetched successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setRateLoading(false);
    }
  };

  useEffect(() => {
    const settingsData = globalSettings || {};
    const dataSettings = {};
    if (Array.isArray(data)) {
      data.forEach((obj) => {
        if (obj.name) {
          dataSettings[obj.name] = obj.value;
        }
      });
    }

    const allSettings = { ...dataSettings, ...settingsData };

    formik.setValues({
      RESEND_OTP_TIME: allSettings?.RESEND_OTP_TIME || "",
      LOGOUT_TIME_IN_MINUTE: allSettings?.LOGOUT_TIME_IN_MINUTE || "",
      ORGANIZATION_MID: allSettings?.ORGANIZATION_MID || "",
      ORGANIZATION_ACCESS_TOKEN: allSettings?.ORGANIZATION_ACCESS_TOKEN || "",
      ADMIN_EMAIL: allSettings?.ADMIN_EMAIL || "",
      USD_EXCHANGE_RATE: currencyRates.usd.rate,
      INR_EXCHANGE_RATE: currencyRates.inr.rate,
      MAILER: allSettings?.MAILER || "",
      SMTP_HOST: allSettings?.SMTP_HOST || "",
      SMTP_PORT: allSettings?.SMTP_PORT || "",
      SMTP_USER: allSettings?.SMTP_USER || "",
      SMTP_PASSWORD: allSettings?.SMTP_PASSWORD || "",
      SMTP_ENCRYPTION_TYPE: allSettings?.SMTP_ENCRYPTION_TYPE || "",
      MAIL_API_URL: allSettings?.MAIL_API_URL || "",
      MAIL_API_TOKEN: allSettings?.MAIL_API_TOKEN || "",
      MAIL_API_SITE_ID: allSettings?.MAIL_API_SITE_ID || "",
      IP_BLOCK: allSettings?.IP_BLOCK || "off",
      WHITE_LIST_IP: allSettings?.WHITE_LIST_IP || "off",
    });
  }, [globalSettings, data, currencyRates]);

  const updateLocalstorage = async (userDataFromLocalStorage) => {
    const encryptedUserData = CryptoJS.AES.encrypt(
      JSON.stringify(userDataFromLocalStorage),
      CRYPTO_TOKEN_SECRET_KEY
    ).toString();
    localStorage.setItem("userData", encryptedUserData);
  };

  const formik = useFormik({
    initialValues: {
      RESEND_OTP_TIME: "",
      LOGOUT_TIME_IN_MINUTE: "",
      ORGANIZATION_MID: "",
      ORGANIZATION_ACCESS_TOKEN: "",
      ADMIN_EMAIL: "",
      USD_EXCHANGE_RATE: "1.1900",
      INR_EXCHANGE_RATE: "105.8500",
      MAILER: "",
      SMTP_HOST: "",
      SMTP_PORT: "",
      SMTP_USER: "",
      SMTP_PASSWORD: "",
      SMTP_ENCRYPTION_TYPE: "",
      MAIL_API_URL: "",
      MAIL_API_TOKEN: "",
      MAIL_API_SITE_ID: "",
      IP_BLOCK: "off",
      WHITE_LIST_IP: "off",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setButtonDisable(true);
      setLoading(true);

      try {
        // Extract currency rates from form values
        const { USD_EXCHANGE_RATE, INR_EXCHANGE_RATE, ...settingsToUpdate } = values;

        // Update currency rates
        const currencyUpdateData = {
          usd: { id: currencyRates.usd.id, exchangeRate: USD_EXCHANGE_RATE },
          inr: { id: currencyRates.inr.id, exchangeRate: INR_EXCHANGE_RATE },
        };

        await editCurrencyRatesAction(currencyUpdateData);

        // Update global settings
        await updateGlobalSettingAction(settingsToUpdate);

        // Fetch fresh data after successful update
        const [freshSettings, freshCurrencyRates] = await Promise.all([
          fetchGlobalSettings(),
          fetchCurrencyRates()
        ]);

        // Update user data in localStorage
        if (userData?.settingData) {
          userData.settingData = freshSettings;
          await updateLocalstorage(userData);
        }

        setSnackbar({
          open: true,
          message: "Settings updated successfully",
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || error.message || "Failed to update settings",
          severity: "error",
        });
      } finally {
        setLoading(false);
        setButtonDisable(false);
      }
    },
  });

  const trimAndSet = (name, value) => {
    formik.setFieldValue(name, value.replace(/\s/g, ""));
    formik.setFieldTouched(name, true, false);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value.trimStart());
    formik.setFieldTouched(name, true, false);
  };

  const handleNoSpaceChange = (e) => {
    trimAndSet(e.target.name, e.target.value);
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value.replace(/\s/g, ""));
    formik.setFieldTouched(name, true, false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value.replace(/\s/g, ""));
    formik.setFieldTouched(name, true, false);
  };

  const isFormValid = formik.isValid && Object.keys(formik.touched).length > 0;

  return (
    <>
      {pageLoading && <Loader />}

      {/* Container matching standard full width of page wrapper */}
      <div id="kt_content_container" style={{ width: "100%", padding: 0 }}>

        <Box sx={{ mb: 3 }}>
          <BreadcrumbsComponent />
        </Box>
        
        <Box
          sx={{
            bgcolor: "#f8fafc",
            borderRadius: 2,
            p: 3,
          }}
        >

        <form onSubmit={formik.handleSubmit} className="row g-5">
          <div className="col-12">

            {/* Card 1: General Settings */}
            <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "#e2e8f0", boxShadow: "none", bgcolor: "#ffffff", mb: 4, width: "100%" }}>
              <Box sx={{ borderBottom: "1px solid #f1f5f9", px: 3, py: 2, bgcolor: "#fafafa" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
                  General Settings
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                  Adjust system OTP times, logout triggers, and API credentials.
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>

                  {/* Resend OTP */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Resend OTP Time (in Seconds) <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter resend OTP time"
                      size="small"
                      id="RESEND_OTP_TIME"
                      name="RESEND_OTP_TIME"
                      value={formik.values.RESEND_OTP_TIME}
                      onChange={handleNoSpaceChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.RESEND_OTP_TIME && Boolean(formik.errors.RESEND_OTP_TIME)}
                      helperText={formik.touched.RESEND_OTP_TIME && formik.errors.RESEND_OTP_TIME}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Logout Time */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Logout Time (in Minutes) <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter logout time"
                      size="small"
                      id="LOGOUT_TIME_IN_MINUTE"
                      name="LOGOUT_TIME_IN_MINUTE"
                      value={formik.values.LOGOUT_TIME_IN_MINUTE}
                      onChange={handleNoSpaceChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.LOGOUT_TIME_IN_MINUTE && Boolean(formik.errors.LOGOUT_TIME_IN_MINUTE)}
                      helperText={formik.touched.LOGOUT_TIME_IN_MINUTE && formik.errors.LOGOUT_TIME_IN_MINUTE}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Plug & Play MID */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Plug & Play (MID) <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter plug & play (MID)"
                      size="small"
                      id="ORGANIZATION_MID"
                      name="ORGANIZATION_MID"
                      value={formik.values.ORGANIZATION_MID}
                      onChange={handleNoSpaceChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ORGANIZATION_MID && Boolean(formik.errors.ORGANIZATION_MID)}
                      helperText={formik.touched.ORGANIZATION_MID && formik.errors.ORGANIZATION_MID}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Access Token */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Plug & Play (Access Token) <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter plug & play (Access Token)"
                      size="small"
                      id="ORGANIZATION_ACCESS_TOKEN"
                      name="ORGANIZATION_ACCESS_TOKEN"
                      value={formik.values.ORGANIZATION_ACCESS_TOKEN}
                      onChange={handleNoSpaceChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ORGANIZATION_ACCESS_TOKEN && Boolean(formik.errors.ORGANIZATION_ACCESS_TOKEN)}
                      helperText={formik.touched.ORGANIZATION_ACCESS_TOKEN && formik.errors.ORGANIZATION_ACCESS_TOKEN}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Support Email */}
                  <Grid item xs={12} md={12}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Admin Support Email <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter Admin support email"
                      size="small"
                      id="ADMIN_EMAIL"
                      name="ADMIN_EMAIL"
                      type="text"
                      value={formik.values.ADMIN_EMAIL}
                      onChange={handleNoSpaceChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ADMIN_EMAIL && Boolean(formik.errors.ADMIN_EMAIL)}
                      helperText={formik.touched.ADMIN_EMAIL && formik.errors.ADMIN_EMAIL}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                </Grid>
              </CardContent>
            </Card>

            {/* Card 2: Currency Rates */}
            <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "#e2e8f0", boxShadow: "none", bgcolor: "#ffffff", mb: 4, width: "100%" }}>
              <Box sx={{ borderBottom: "1px solid #f1f5f9", px: 3, py: 2, bgcolor: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
                    Currency Conversion Rates
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5 }}>
                    Manage and synchronize live exchange conversion rates.
                  </Typography>
                </Box>

                <button
                  type="button"
                  onClick={fetchLatestRates}
                  disabled={rateLoading}
                  style={{
                    backgroundColor: "#ffffff",
                    color: "#0f172a",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: rateLoading ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: "var(--app-font)",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseOver={(e) => { if (!rateLoading) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                  onMouseOut={(e) => { if (!rateLoading) e.currentTarget.style.backgroundColor = "#ffffff"; }}
                >
                  {rateLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Fetch New Rate
                </button>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>

                  {/* USD Editable Input */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      KYD to USD Conversion Rate <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter KYD rate"
                      size="small"
                      id="USD_EXCHANGE_RATE"
                      name="USD_EXCHANGE_RATE"
                      value={formik.values.USD_EXCHANGE_RATE}
                      onChange={handleRateChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.USD_EXCHANGE_RATE && Boolean(formik.errors.USD_EXCHANGE_RATE)}
                      helperText={formik.touched.USD_EXCHANGE_RATE && formik.errors.USD_EXCHANGE_RATE}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Current USD display */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Current USD Conversion Rate
                    </Typography>
                    <Box sx={{
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      bgcolor: "#f8fafc",
                      border: "1px dashed #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#334155",
                      fontFamily: "var(--app-font)"
                    }}>
                      ${currentRates.usd}
                    </Box>
                  </Grid>

                  {/* INR Editable Input */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      KYD to INR Conversion Rate <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      placeholder="Enter KYD rate"
                      size="small"
                      id="INR_EXCHANGE_RATE"
                      name="INR_EXCHANGE_RATE"
                      value={formik.values.INR_EXCHANGE_RATE}
                      onChange={handleRateChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.INR_EXCHANGE_RATE && Boolean(formik.errors.INR_EXCHANGE_RATE)}
                      helperText={formik.touched.INR_EXCHANGE_RATE && formik.errors.INR_EXCHANGE_RATE}
                      fullWidth
                      sx={TEXTFIELD_STYLE}
                    />
                  </Grid>

                  {/* Current INR display */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Current INR Conversion Rate
                    </Typography>
                    <Box sx={{
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      bgcolor: "#f8fafc",
                      border: "1px dashed #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#334155",
                      fontFamily: "var(--app-font)"
                    }}>
                      ₹{currentRates.inr}
                    </Box>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>

            {/* Card 3: Mail Configuration */}
            <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "#e2e8f0", boxShadow: "none", bgcolor: "#ffffff", mb: 4, width: "100%" }}>
              <Box sx={{ borderBottom: "1px solid #f1f5f9", px: 3, py: 2, bgcolor: "#fafafa" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
                  Mail Configuration
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                  Configure primary mail dispatchers (SMTP or Web APIs).
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>

                  {/* Mailer dropdown */}
                  <Grid item xs={12} md={12}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                      Mailer <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        id="MAILER"
                        name="MAILER"
                        value={formik.values.MAILER}
                        onChange={handleFieldChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.MAILER && Boolean(formik.errors.MAILER)}
                        displayEmpty
                        sx={SELECT_STYLE}
                      >
                        <MenuItem value="" disabled>
                          Select Mailer
                        </MenuItem>
                        <MenuItem value="Mail API">Mail API</MenuItem>
                        <MenuItem value="SMTP">SMTP</MenuItem>
                      </Select>
                      {formik.touched.MAILER && formik.errors.MAILER && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                          {formik.errors.MAILER}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* SMTP specific fields */}
                  {formik.values.MAILER === "SMTP" && (
                    <>
                      {/* SMTP Host */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          SMTP Host <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          id="SMTP_HOST"
                          name="SMTP_HOST"
                          value={formik.values.SMTP_HOST}
                          onChange={handleNoSpaceChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.SMTP_HOST && Boolean(formik.errors.SMTP_HOST)}
                          helperText={formik.touched.SMTP_HOST && formik.errors.SMTP_HOST}
                          fullWidth
                          placeholder="smtp.gmail.com"
                          sx={TEXTFIELD_STYLE}
                        />
                      </Grid>

                      {/* SMTP Port */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          SMTP Port <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          id="SMTP_PORT"
                          name="SMTP_PORT"
                          value={formik.values.SMTP_PORT}
                          onChange={handleNoSpaceChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.SMTP_PORT && Boolean(formik.errors.SMTP_PORT)}
                          helperText={formik.touched.SMTP_PORT && formik.errors.SMTP_PORT}
                          fullWidth
                          placeholder="587"
                          sx={TEXTFIELD_STYLE}
                        />
                      </Grid>

                      {/* SMTP User */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          SMTP User <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          id="SMTP_USER"
                          name="SMTP_USER"
                          value={formik.values.SMTP_USER}
                          onChange={handleNoSpaceChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.SMTP_USER && Boolean(formik.errors.SMTP_USER)}
                          helperText={formik.touched.SMTP_USER && formik.errors.SMTP_USER}
                          fullWidth
                          placeholder="your-email@gmail.com"
                          sx={TEXTFIELD_STYLE}
                        />
                      </Grid>

                      {/* SMTP Password */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          SMTP Password <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          placeholder="Enter SMTP password"
                          size="small"
                          id="SMTP_PASSWORD"
                          name="SMTP_PASSWORD"
                          type={showSmtpPassword ? "text" : "password"}
                          value={formik.values.SMTP_PASSWORD}
                          onChange={handlePasswordChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.SMTP_PASSWORD && Boolean(formik.errors.SMTP_PASSWORD)}
                          helperText={formik.touched.SMTP_PASSWORD && formik.errors.SMTP_PASSWORD}
                          fullWidth
                          sx={TEXTFIELD_STYLE}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                                  edge="end"
                                  size="small"
                                >
                                  {showSmtpPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* Encryption Type */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          Encryption Type <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            id="SMTP_ENCRYPTION_TYPE"
                            name="SMTP_ENCRYPTION_TYPE"
                            value={formik.values.SMTP_ENCRYPTION_TYPE}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.SMTP_ENCRYPTION_TYPE && Boolean(formik.errors.SMTP_ENCRYPTION_TYPE)}
                            displayEmpty
                            sx={SELECT_STYLE}
                          >
                            <MenuItem value="" disabled>Select Encryption Type</MenuItem>
                            <MenuItem value="TLS">TLS</MenuItem>
                            <MenuItem value="SSL">SSL</MenuItem>
                            <MenuItem value="None">None</MenuItem>
                          </Select>
                          {formik.touched.SMTP_ENCRYPTION_TYPE && formik.errors.SMTP_ENCRYPTION_TYPE && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {formik.errors.SMTP_ENCRYPTION_TYPE}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  {/* Mail API specific fields */}
                  {formik.values.MAILER === "Mail API" && (
                    <>
                      {/* API URL */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          API URL <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          id="MAIL_API_URL"
                          name="MAIL_API_URL"
                          value={formik.values.MAIL_API_URL}
                          onChange={handleNoSpaceChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.MAIL_API_URL && Boolean(formik.errors.MAIL_API_URL)}
                          helperText={formik.touched.MAIL_API_URL && formik.errors.MAIL_API_URL}
                          fullWidth
                          placeholder="https://api.example.com"
                          sx={TEXTFIELD_STYLE}
                        />
                      </Grid>

                      {/* Token */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          Token <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          placeholder="Enter token"
                          size="small"
                          id="MAIL_API_TOKEN"
                          name="MAIL_API_TOKEN"
                          type={showMailApiToken ? "text" : "password"}
                          value={formik.values.MAIL_API_TOKEN}
                          onChange={handlePasswordChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.MAIL_API_TOKEN && Boolean(formik.errors.MAIL_API_TOKEN)}
                          helperText={formik.touched.MAIL_API_TOKEN && formik.errors.MAIL_API_TOKEN}
                          fullWidth
                          sx={TEXTFIELD_STYLE}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowMailApiToken(!showMailApiToken)}
                                  edge="end"
                                  size="small"
                                >
                                  {showMailApiToken ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* Site ID */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155", mb: 1, fontSize: "12px" }}>
                          Site ID <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          id="MAIL_API_SITE_ID"
                          name="MAIL_API_SITE_ID"
                          value={formik.values.MAIL_API_SITE_ID}
                          onChange={handleNoSpaceChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.MAIL_API_SITE_ID && Boolean(formik.errors.MAIL_API_SITE_ID)}
                          helperText={formik.touched.MAIL_API_SITE_ID && formik.errors.MAIL_API_SITE_ID}
                          fullWidth
                          placeholder="site-id-123"
                          sx={TEXTFIELD_STYLE}
                        />
                      </Grid>
                    </>
                  )}

                </Grid>
              </CardContent>
            </Card>

            {/* Card 4: IP Security */}
            <Card variant="outlined" sx={{ borderRadius: "12px", borderColor: "#e2e8f0", boxShadow: "none", bgcolor: "#ffffff", mb: 4, width: "100%" }}>
              <Box sx={{ borderBottom: "1px solid #f1f5f9", px: 3, py: 2, bgcolor: "#fafafa" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
                  IP Security
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                  Secure endpoints by whitelist control and blocking brute-force IPs.
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>

                  {/* IP Block Switch */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      p: 2,
                      border: "1px solid #f1f5f9",
                      borderRadius: "8px",
                      bgcolor: "#fafafa",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155", fontSize: "13px" }}>
                          IP Block
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                          Block IP addresses after multiple failed log-in attempts.
                        </Typography>
                      </Box>
                      <Switch
                        checked={formik.values.IP_BLOCK === "on"}
                        onChange={(e) => {
                          formik.setFieldValue("IP_BLOCK", e.target.checked ? "on" : "off");
                          formik.setFieldTouched("IP_BLOCK", true);
                        }}
                        sx={SWITCH_STYLE}
                      />
                    </Box>
                  </Grid>

                  {/* Whitelist IP Switch */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      p: 2,
                      border: "1px solid #f1f5f9",
                      borderRadius: "8px",
                      bgcolor: "#fafafa",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155", fontSize: "13px" }}>
                          Whitelist IP
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                          Restrict log-in events to whitelisted network boundaries only.
                        </Typography>
                      </Box>
                      <Switch
                        checked={formik.values.WHITE_LIST_IP === "on"}
                        onChange={(e) => {
                          formik.setFieldValue("WHITE_LIST_IP", e.target.checked ? "on" : "off");
                          formik.setFieldTouched("WHITE_LIST_IP", true);
                        }}
                        sx={SWITCH_STYLE}
                      />
                    </Box>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>

            {/* Action Row */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
              {hasPermission?.canEdit && (
                <button
                  type="submit"
                  disabled={buttonDisable || loading || !isFormValid}
                  style={{
                    backgroundColor: (buttonDisable || loading || !isFormValid) ? "#f1f5f9" : "#0f172a",
                    color: (buttonDisable || loading || !isFormValid) ? "#94a3b8" : "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: (buttonDisable || loading || !isFormValid) ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: "var(--app-font)",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!buttonDisable && !loading && isFormValid) {
                      e.currentTarget.style.backgroundColor = "#1e293b";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!buttonDisable && !loading && isFormValid) {
                      e.currentTarget.style.backgroundColor = "#0f172a";
                    }
                  }}
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update Settings
                </button>
              )}
            </Box>

          </div>
        </form>

        </Box>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        />
      </div>
    </>
  );
};

export default SettingsSection;